import {
  GlobeViewMapType,
  IGlobeViewStyle,
} from '~/client/graph/types.generated'

// What a map surface's settings are, and how the parts that are the same everywhere are derived
// from a saved view's style. Three stores host a map and each used to answer these questions
// itself — which is how a cross-cutting concern gets threaded into two hosts and forgotten in
// the third. The type lives here beside the derivation rather than in the panel that renders it.

export interface IMapSettings {
  mapType: 'street' | 'satellite'
  isThreeDEnabled: boolean
  isTrafficEnabled: boolean
  isTerrainEnabled: boolean
  isZoomScalingEnabled?: boolean
  isLabelOverlapEnabled?: boolean
  shouldHideLabels: boolean
  shouldHideWorkflowPinLabels?: boolean
  isShowWorkflowMarkupsEnabled?: boolean
  isShowWorkflowMarkupLabelsEnabled?: boolean
  isWorkflowMarkupsProjectDefault?: boolean
  isShowAdjacentMarkupsEnabled?: boolean
  onMapTypeChange: () => void
  onToggle3D: () => void
  onToggleTraffic: () => void
  onToggleTerrain: () => void
  onToggleZoomScaling?: () => void
  onToggleLabelOverlap?: () => void
  onToggleWorkflowPinLabels?: () => void
  onToggleLabels: () => void
  onToggleShowWorkflowMarkups?: () => void
  onToggleShowWorkflowMarkupLabels?: () => void
  onToggleWorkflowMarkupsProjectDefault?: () => void
  onToggleShowAdjacentMarkups?: () => void
}

// The callbacks are NOT derived: each store wires its own, and past these fields the three
// mapSettings genuinely diverge — markup toggles, admin checks, project defaults. This is
// exactly the part that is the same.
export type IMapStyleSettings = Pick<
  IMapSettings,
  | 'mapType'
  | 'isThreeDEnabled'
  | 'isTrafficEnabled'
  | 'isTerrainEnabled'
  | 'isZoomScalingEnabled'
  | 'isLabelOverlapEnabled'
  | 'shouldHideLabels'
  | 'shouldHideWorkflowPinLabels'
>

// Takes only the field it reads, so a host whose style is a narrower subset — MapControlsStore
// carries five fields, not the generated eight — can use it too.
export function isSatelliteStyle(style: {
  mapType: GlobeViewMapType
}): boolean {
  return style.mapType === GlobeViewMapType.Satellite
}

export function mapStyleSettingsFrom(
  style: IGlobeViewStyle,
): IMapStyleSettings {
  return {
    mapType: isSatelliteStyle(style) ? 'satellite' : 'street',
    isThreeDEnabled: style.isThreeDEnabled,
    isTrafficEnabled: style.isTrafficEnabled,
    isTerrainEnabled: style.isTerrainEnabled,
    isZoomScalingEnabled: style.isZoomScalingEnabled,
    isLabelOverlapEnabled: style.isLabelOverlapEnabled,
    shouldHideLabels: style.shouldHideLabels,
    shouldHideWorkflowPinLabels: style.shouldHideWorkflowPinLabels,
  }
}
